import { useState } from 'react';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { day } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { Editor } from './Marketing.tsx';
import { Kpi } from './Reports.tsx';

/**
 * Journeys: a series of e-mails each person gets on their own clock, from the day they come
 * into an audience. The page shows the series as a timeline (day 0, day 3, day 10...), how
 * each e-mail did, and who is where in it.
 */

type JState = 'draft' | 'active' | 'paused';
type MState = 'active' | 'done' | 'left';
interface JourneyRow {
  id: string;
  name: string;
  state: JState;
  description: string | null;
  audience: string | null;
  audience_name: string | null;
  steps: number;
  sent: number;
  opened: number;
  active: number;
  done: number;
}
interface StepRow {
  id: string;
  number: number;
  subject: string;
  empty: boolean;
  delay_days: number;
  day: number;
  waiting: number;
  sent: number;
  opened: number;
  clicked: number;
  unsubscribed: number;
  failed: number;
}
interface Journey extends Omit<JourneyRow, 'steps' | 'sent' | 'opened' | 'active' | 'done'> {
  started_at: string | null;
  people: { active: number; done: number; left: number; total: number };
  steps: StepRow[];
}
interface Member {
  id: string;
  name: string;
  email: string | null;
  step: number;
  next_at: string | null;
  state: MState;
  left_reason: 'no_longer_fits' | 'stopped_emails' | null;
  joined_at: string;
}
interface AudienceOption {
  id: string;
  name: string;
  reachable: number;
}

const STATE: Record<JState, { tone: Tone; icon: IconName }> = {
  draft: { tone: 'neutral', icon: 'edit' },
  active: { tone: 'ok', icon: 'play' },
  paused: { tone: 'warn', icon: 'pause' },
};
const pct = (part: number, whole: number) => (whole ? `${Math.round((part / whole) * 1000) / 10}%` : '—');

function JourneyBadge({ state }: { state: JState }) {
  return <StatusBadge tone={STATE[state].tone} icon={STATE[state].icon} label={t(`jr.st_${state}` as MessageKey)} pulse={state === 'active'} />;
}

export function JourneysTab({ params, set }: { params: URLSearchParams; set: (n: Record<string, string | null>) => void }) {
  const { can } = useSession();
  const { data, reload } = useAction<JourneyRow[]>('marketing.journeys');
  const [creating, setCreating] = useState(false);
  const open = params.get('j');
  const manage = can('marketing.manage');
  return (
    <section className="panel">
      <div className="prot-history-head">
        <div>
          <h2 className="rep-sub">{t('jr.title')}</h2>
          <p className="muted small">{t('jr.hint')}</p>
        </div>
        {manage ? (
          <Button size="sm" icon="plus" onClick={() => setCreating(true)}>
            {t('jr.new')}
          </Button>
        ) : null}
      </div>
      {!data ? (
        <Skeleton h={200} />
      ) : data.length ? (
        <ul className="jr-list">
          {data.map((j) => (
            <li key={j.id}>
              <button type="button" className="jr-card" onClick={() => set({ j: j.id })}>
                <span className="jr-card-head">
                  <strong>{j.name}</strong>
                  <JourneyBadge state={j.state} />
                </span>
                <span className="muted small">
                  {[j.audience_name ? t('jr.for_audience', { name: j.audience_name }) : t('jr.no_audience'), tn('jr.n_emails', j.steps)].join(' · ')}
                </span>
                <span className="jr-card-stats">
                  <span>
                    <strong className="tabular">{j.active}</strong> <span className="muted small">{t('jr.in_now')}</span>
                  </span>
                  <span>
                    <strong className="tabular">{j.done}</strong> <span className="muted small">{t('jr.finished')}</span>
                  </span>
                  <span>
                    <strong className="tabular">{pct(j.opened, j.sent)}</strong> <span className="muted small">{t('jr.opened')}</span>
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState
          icon="repeat"
          title={t('jr.empty')}
          body={t('jr.empty_hint')}
          action={
            manage ? (
              <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
                {t('jr.new')}
              </Button>
            ) : undefined
          }
        />
      )}
      {creating ? (
        <JourneyForm
          j={null}
          onClose={() => setCreating(false)}
          onSaved={async (id) => {
            setCreating(false);
            await reload();
            set({ j: id });
          }}
        />
      ) : null}
      {open ? <JourneyDialog key={open} id={open} onClose={() => set({ j: null })} onChanged={reload} /> : null}
    </section>
  );
}

function JourneyForm({ j, onClose, onSaved }: { j: Pick<Journey, 'id' | 'name' | 'audience' | 'description'> | null; onClose: () => void; onSaved: (id: string) => Promise<void> }) {
  const { data: setup } = useAction<{ audiences: AudienceOption[] }>('marketing.setup');
  const [name, setName] = useState(j?.name ?? '');
  const [audience, setAudience] = useState(j?.audience ?? '');
  const [description, setDescription] = useState(j?.description ?? '');
  const [tried, setTried] = useState(false);
  const { busy, run } = useSave();
  const errors = { name: !name.trim() ? t('mkt.e_name') : null, audience: !audience ? t('jr.e_audience') : null };
  const aud = setup?.audiences.find((a) => a.id === audience);
  const save = () => {
    setTried(true);
    if (errors.name || errors.audience) return;
    void run(async () => {
      const out = await action<{ id: string }>('marketing.save_journey', { id: j?.id ?? null, name: name.trim(), audience, description: description.trim() || null });
      await onSaved(out.id);
    }, t('jr.saved'));
  };
  return (
    <Dialog
      title={j ? t('jr.edit') : t('jr.new')}
      onClose={onClose}
      footer={
        <Button variant="primary" icon="check" busy={busy} onClick={save}>
          {t('common.save')}
        </Button>
      }
    >
      <div className="form-stack">
        <Field label={t('mkt.name')} required error={tried ? errors.name : null} hint={t('jr.name_hint')}>
          <input className="input" value={name} maxLength={120} placeholder={t('jr.name_example')} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label={t('jr.who')} required error={tried ? errors.audience : null} hint={aud ? tn('mkt.n_agreed', aud.reachable) : t('jr.who_hint')}>
          {!setup ? (
            <Skeleton h={40} />
          ) : (
            <select className="input" value={audience} onChange={(e) => setAudience(e.target.value)}>
              <option value="">{t('jr.choose_audience')}</option>
              {setup.audiences.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} ({a.reachable})
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label={t('jr.description')} counter={`${description.length}/500`}>
          <textarea className="input" rows={2} maxLength={500} value={description} placeholder={t('jr.description_example')} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <p className="muted small">
          <Icon name="info" size={14} /> {t('jr.leave_rule')}
        </p>
      </div>
    </Dialog>
  );
}

const M_FILTERS = ['active', 'done', 'left'] as const;

function JourneyDialog({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => Promise<unknown> }) {
  const { can } = useSession();
  const { data: j, reload } = useAction<Journey>('marketing.journey', { id });
  const [editing, setEditing] = useState(false);
  const [writing, setWriting] = useState<{ id: string; n: number } | null>(null);
  const [filter, setFilter] = useState<MState>('active');
  const [ver, setVer] = useState(0);
  const { busy, run } = useSave();
  const manage = can('marketing.manage');
  const send = can('marketing.send');
  const changed = async () => {
    setVer((v) => v + 1);
    await reload();
    await onChanged();
  };
  if (!j) {
    return (
      <Dialog title={t('common.loading')} onClose={onClose} size="lg">
        <Skeleton h={320} />
      </Dialog>
    );
  }
  if (editing) return <JourneyForm j={j} onClose={() => setEditing(false)} onSaved={async () => (setEditing(false), changed())} />;
  const sent = j.steps.reduce((s, x) => s + x.sent, 0);
  const opened = j.steps.reduce((s, x) => s + x.opened, 0);
  const clicked = j.steps.reduce((s, x) => s + x.clicked, 0);
  const addStep = () =>
    run(async () => {
      const s = await action<{ id: string }>('marketing.add_step', { journey: j.id, delay_days: j.steps.length ? 3 : 0 });
      await changed();
      setWriting({ id: s.id, n: j.steps.length + 1 });
    });
  const setDelay = (s: StepRow, n: number) => {
    if (n === s.delay_days) return;
    void run(async () => {
      await action('marketing.save_step', { id: s.id, delay_days: n });
      await changed();
    }, t('jr.wait_saved'));
  };
  const state = (next: 'active' | 'paused') =>
    run(
      async () => {
        await action('marketing.journey_state', { id: j.id, state: next });
        await changed();
      },
      next === 'active' ? t('jr.started') : t('jr.paused'),
    );
  return (
    <Dialog
      title={j.name}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <span className="foot-actions">
            {manage ? (
              <Button variant="ghost" icon="edit" onClick={() => setEditing(true)}>
                {t('jr.edit')}
              </Button>
            ) : null}
            {manage && !j.people.total ? (
              <Button
                variant="ghost"
                icon="trash"
                busy={busy}
                onClick={async () => {
                  if (!window.confirm(t('jr.delete_confirm', { name: j.name }))) return;
                  if (await run(() => action('marketing.delete_journey', { id: j.id }), t('jr.deleted'))) {
                    await onChanged();
                    onClose();
                  }
                }}
              >
                {t('jr.delete')}
              </Button>
            ) : null}
          </span>
          {send ? (
            j.state === 'active' ? (
              <Button icon="pause" busy={busy} onClick={() => void state('paused')}>
                {t('jr.pause')}
              </Button>
            ) : (
              <Button variant="primary" icon="play" busy={busy} disabled={!j.steps.length || j.steps.some((s) => s.empty)} onClick={() => void state('active')}>
                {j.state === 'paused' ? t('jr.resume') : t('jr.start')}
              </Button>
            )
          ) : null}
        </>
      }
    >
      <div className="form-stack">
        <div className="jr-head">
          <JourneyBadge state={j.state} />
          <span className="muted small">
            {j.audience_name ? t('jr.for_audience', { name: j.audience_name }) : t('jr.no_audience')}
            {j.started_at ? ` · ${t('jr.since', { date: day(j.started_at.slice(0, 10)) })}` : ''}
          </span>
        </div>
        {j.description ? <p className="muted">{j.description}</p> : null}
        {j.state !== 'active' && send && j.steps.some((s) => s.empty) ? (
          <p className="callout callout-warn">
            <Icon name="alert" size={16} />
            <span>{t('jr.write_first')}</span>
          </p>
        ) : null}
        <section className="rep-kpis" aria-label={t('mkt.at_a_glance')}>
          <Kpi label={t('jr.k_in')} value={String(j.people.active)} hint={tn('jr.k_joined', j.people.total)} />
          <Kpi label={t('jr.k_done')} value={String(j.people.done)} hint={j.people.left ? tn('jr.k_left', j.people.left) : t('jr.k_nobody_left')} />
          <Kpi label={t('jr.k_open')} value={pct(opened, sent)} hint={tn('jr.k_sent', sent)} />
          <Kpi label={t('jr.k_click')} value={pct(clicked, sent)} hint={tn('mkt.k_clicked', clicked)} />
        </section>
        <section className="panel" aria-labelledby="jr-steps">
          <div className="panel-head">
            <h2 id="jr-steps">{t('jr.series')}</h2>
            {manage ? (
              <Button size="sm" icon="plus" busy={busy} onClick={() => void addStep()} disabled={j.steps.length >= 20}>
                {t('jr.add_email')}
              </Button>
            ) : null}
          </div>
          {!j.steps.length ? (
            <EmptyState icon="mail" title={t('jr.no_steps')} body={t('jr.no_steps_hint')} />
          ) : (
            <ol className="jr-timeline">
              <li className="jr-start">
                <span className="jr-dot" aria-hidden="true">
                  <Icon name="users" size={14} />
                </span>
                <span className="muted small">{t('jr.joins')}</span>
              </li>
              {j.steps.map((s, i) => (
                <li key={s.id} className={s.empty ? 'empty' : ''}>
                  <span className="jr-wait">
                    <Icon name="clock" size={14} />
                    {manage ? (
                      <label className="jr-wait-edit">
                        <span>{t('jr.wait')}</span>
                        <input
                          key={`${s.id}-${s.delay_days}-${ver}`}
                          className="input tabular"
                          inputMode="numeric"
                          aria-label={t('jr.wait_days_for', { n: i + 1 })}
                          defaultValue={String(s.delay_days)}
                          onBlur={(e) => setDelay(s, Math.min(365, Number(e.target.value.replace(/\D/g, '') || 0)))}
                          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                        />
                        <span>{t('jr.days')}</span>
                      </label>
                    ) : (
                      <span>{s.delay_days ? tn('jr.after_days', s.delay_days) : t('jr.right_away')}</span>
                    )}
                  </span>
                  <div className="jr-step">
                    <span className="jr-dot" aria-hidden="true">
                      {i + 1}
                    </span>
                    <div className="jr-step-body">
                      <span className="jr-step-head">
                        <span className="muted small">{t('jr.day_n', { n: s.day })}</span>
                        <strong>{s.empty ? t('jr.not_written') : s.subject}</strong>
                      </span>
                      <span className="jr-step-stats small">
                        <span>{tn('jr.s_sent', s.sent)}</span>
                        <span>{t('jr.s_opened', { p: pct(s.opened, s.sent) })}</span>
                        <span>{t('jr.s_clicked', { p: pct(s.clicked, s.sent) })}</span>
                        {s.waiting ? <span className="strong">{tn('jr.s_waiting', s.waiting)}</span> : null}
                      </span>
                    </div>
                    {manage ? (
                      <span className="row-actions">
                        <Button size="sm" variant={s.empty ? 'primary' : 'ghost'} icon="edit" onClick={() => setWriting({ id: s.id, n: i + 1 })}>
                          {s.empty ? t('jr.write') : t('jr.edit_email')}
                        </Button>
                        {!s.sent && !s.failed ? (
                          <IconButton
                            icon="trash"
                            label={t('jr.remove_email', { n: i + 1 })}
                            onClick={() =>
                              void run(async () => {
                                await action('marketing.remove_step', { id: s.id });
                                await changed();
                              }, t('jr.removed'))
                            }
                          />
                        ) : null}
                      </span>
                    ) : null}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </section>
        <section className="panel" aria-labelledby="jr-people">
          <div className="panel-head">
            <h2 id="jr-people">{t('jr.people')}</h2>
            <div className="segmented subtle" role="tablist" aria-label={t('jr.people')}>
              {M_FILTERS.map((f) => (
                <button key={f} role="tab" aria-selected={filter === f} className={filter === f ? 'active' : ''} onClick={() => setFilter(f)}>
                  {t(`jr.f_${f}` as MessageKey)}
                </button>
              ))}
            </div>
          </div>
          <People journey={j.id} filter={filter} steps={j.steps.length} version={ver} />
        </section>
      </div>
      {writing ? (
        <Editor
          id={writing.id}
          step={{ label: t('jr.email_n_of', { n: writing.n, name: j.name }) }}
          onClose={() => setWriting(null)}
          onSaved={async () => {
            setWriting(null);
            await changed();
          }}
        />
      ) : null}
    </Dialog>
  );
}

function People({ journey, filter, steps, version }: { journey: string; filter: MState; steps: number; version: number }) {
  const { data } = useAction<{ total: number; rows: Member[] }>('marketing.journey_members', { journey, filter, v: version });
  if (!data) return <Skeleton h={120} />;
  if (!data.rows.length) return <p className="muted small">{t(`jr.none_${filter}` as MessageKey)}</p>;
  return (
    <>
      <ul className="jr-people">
        {data.rows.map((m) => (
          <li key={m.id}>
            <span>
              <span className="strong">{m.name}</span>
              {m.email ? <span className="muted small"> · {m.email}</span> : null}
            </span>
            <span className="muted small">
              {m.state === 'active'
                ? m.next_at
                  ? t('jr.next_email', { n: Math.min(m.step + 1, steps), date: day(m.next_at.slice(0, 10)) })
                  : t('jr.waiting')
                : m.state === 'done'
                  ? t('jr.got_all')
                  : t(`jr.left_${m.left_reason ?? 'no_longer_fits'}` as MessageKey)}
            </span>
          </li>
        ))}
      </ul>
      {data.total > data.rows.length ? <p className="muted small">{t('jr.more_people', { n: data.total - data.rows.length })}</p> : null}
    </>
  );
}
